import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MailCheck, CheckCircle2, AlertTriangle, RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";
import { apiVerifyEmail, apiResendVerification, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const RESULT = {
  verified: { ok: true, title: "Email verified", text: "Your email address is confirmed. You now have full access to ChartLenz." },
  already_verified: { ok: true, title: "Already verified", text: "This email address was already confirmed. You can continue." },
  verification_expired: { ok: false, title: "Link expired", text: "This verification link has expired (links are valid for 24 hours). Request a new one below." },
  verification_invalid: { ok: false, title: "Invalid link", text: "This verification link is invalid or has already been replaced by a newer one. Request a new one below." },
};

function Card({ children, testid }) {
  return (
    <section className="pt-16 pb-20 max-w-md mx-auto">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className="border border-neutral-900 bg-neutral-950/60 p-7" data-testid={testid}>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF] mb-3">── email verification ──</div>
        {children}
      </motion.div>
    </section>
  );
}

function ResendButton() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const resend = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await apiResendVerification();
      setMsg(r.status === "already_verified" ? "Your email is already verified." : "A new verification email has been sent.");
      if (r.status === "sent") toast.success("Verification email sent");
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-5">
      <button onClick={resend} disabled={busy} data-testid="verify-resend"
        className="w-full inline-flex items-center justify-center gap-2 border border-neutral-700 hover:border-white text-white font-bold text-xs uppercase tracking-[0.2em] px-6 py-3 transition-colors disabled:opacity-50">
        <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Sending…" : "Resend verification email"}
      </button>
      {msg && <div className="text-xs text-neutral-400 mt-2" data-testid="verify-resend-message">{msg}</div>}
    </div>
  );
}

function TokenResult({ token }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    apiVerifyEmail(token)
      .then(async (r) => { setState(r.status); await refresh(); })
      .catch((e) => setState(e?.response?.data?.detail in RESULT ? e.response.data.detail : "verification_invalid"));
  }, [token, refresh]);

  if (!state) return <Card testid="verify-email-page"><div className="text-xs font-mono text-neutral-500">verifying…</div></Card>;
  const r = RESULT[state];
  return (
    <Card testid="verify-email-page">
      <div className={`flex items-center gap-2 text-sm mb-3 ${r.ok ? "text-[#00FF66]" : "text-[#FFB000]"}`} data-testid="verify-result">
        {r.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {r.title}
      </div>
      <p className="text-sm text-neutral-400 leading-relaxed" data-testid="verify-result-text">{r.text}</p>
      {r.ok ? (
        <button onClick={() => navigate(user ? "/dashboard" : "/login", { replace: true })} data-testid="verify-continue"
          className="mt-6 w-full bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors">
          {user ? "Continue to dashboard" : "Log in"}
        </button>
      ) : user ? <ResendButton /> : (
        <Link to="/login?after=/verify-email" data-testid="verify-login-to-resend" className="mt-6 inline-block text-xs font-mono uppercase tracking-[0.25em] text-neutral-400 hover:text-white">
          log in to request a new link →
        </Link>
      )}
    </Card>
  );
}

function CheckInbox() {
  const { user, loading, logout } = useAuth();
  if (loading) return <Card testid="verify-email-page"><div className="text-xs font-mono text-neutral-500">loading…</div></Card>;
  if (!user) return <Navigate to="/login?after=/verify-email" replace />;
  if (user.email_verified !== false) return <Navigate to="/dashboard" replace />;
  return (
    <Card testid="verify-email-page">
      <div className="flex items-center gap-2 text-sm text-[#00FF66] mb-3" data-testid="verify-sent">
        <MailCheck className="w-4 h-4" /> Verification email sent
      </div>
      <h1 className="text-2xl font-black tracking-tight text-white">Check your inbox</h1>
      <p className="text-sm text-neutral-400 leading-relaxed mt-2">
        We sent a confirmation link to <span className="text-white" data-testid="verify-email-address">{user.email}</span>.
        You need to verify your email before you can use ChartLenz or buy a membership. The link expires in 24 hours.
      </p>
      <p className="text-xs text-neutral-400 mt-3">Didn't get it? Check your spam folder, or request a new link.</p>
      <ResendButton />
      <button onClick={logout} data-testid="verify-logout"
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.25em] text-neutral-500 hover:text-white">
        <LogOut className="w-3.5 h-3.5" /> log out
      </button>
    </Card>
  );
}

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <TokenResult token={token} /> : <CheckInbox />;
}
